import { createAction } from "@reduxjs/toolkit";
import { call, put, select, take, takeEvery } from "redux-saga/effects";
import type { SagaIterator } from "redux-saga";

import { getPostById } from "api/post";
import { openEditPostModal, Post } from "features/post";
import isEmpty from "lodash/isEmpty";
import { toast } from "react-toastify";
import { entitlementFeatureToggle } from "../../utils/feature-toggle-utils";
import {
  LoadedDraft,
  normalizeAnyPost,
} from "../../selectors/loaded-posts-normalizer";
import { openEditPostModalV2 } from "../post/bulk-create/bulk-create-post.actions";

export const opedPostModalById = createAction<{ postId: string }>(
  "opedPostModalById"
);

type RootStateWithUser = {
  user?: unknown;
};

type HttpError = {
  response?: {
    status?: number;
    data?: {
      message?: string;
    };
  };
};

function* openPostById({
  payload: { postId },
}: ReturnType<typeof opedPostModalById>): SagaIterator {
  try {
    const post = yield call(getPostById, postId);
    yield call(waitFor, (state: RootStateWithUser) => state.user);
    if (entitlementFeatureToggle.get("bulk-create")) {
      const editPost: Post | LoadedDraft = yield call(normalizeAnyPost, post);
      yield put(openEditPostModalV2({ post: editPost }));
    } else {
      yield put(openEditPostModal({ post }));
    }
  } catch (e) {
    const error = e as HttpError;
    console.error(error);
    // most common case - post is no longer a draft or has been deleted etc
    if (error.response?.status === 404) {
      toast.error(
        `Sorry, the post or draft with ID ${postId} could not be found. It has been moved or deleted.`
      );
    } else if (error.response?.data?.message) {
      // in case we return a
      toast.error(
        `Sorry, the post or draft with ID ${postId} could not be loaded. ${error.response.data.message}`
      );
    } else {
      toast.error(
        `Sorry, the post or draft with ID ${postId} could not be loaded.`
      );
    }
  }
}

export function* deepLinkPostSaga(): SagaIterator {
  yield takeEvery(opedPostModalById.type, openPostById);
}

export function* waitFor(
  selector: (state: RootStateWithUser) => unknown
): SagaIterator {
  const data = yield select(selector);
  if (!isEmpty(data)) {
    return;
  }
  yield take("*");
  yield call(waitFor, selector);
}
